/**
 * AuthCard — shared wrapper for all auth/activation screens.
 *
 * Usage:
 *   <AuthCard logoUrl={logoUrl} badge="Customer account" poweredBy="Beetsol">
 *     ... your card head + body JSX ...
 *   </AuthCard>
 *
 * The wrapper handles:
 *   - Full-page centering
 *   - Card chrome (white box, border-radius, shadow)
 *   - Logo slot
 *   - Optional badge
 *   - "Powered by" footer
 *   - CSS design tokens (injected once via <style>)
 *
 * Individual card components only need to supply the inner content.
 */

import React from 'react';
import { tokens } from '../../theme';

/* ─── Design tokens ──────────────────────────────────────────────────────── */
const TOKEN_STYLE = `
  :root {
    --bg1: #FFFFFF;
    --bg2: #F7F7F6;
    --bg3: #F0F0EE;
    --t1:  #111110;
    --t2:  #6F6F6C;
    --t3:  #9A9A96;
    --ti:  #111110;
    --bi:  #FAECE7;
    --ts:  #0F6E56;
    --bs:  #E1F5EE;
    --td:  #A32D2D;
    --bd:  #FCEBEB;
    --tw:  #854F0B;
    --bw:  #FAEEDA;
    --b1:  #E8E8E6;
    --b2:  #C8C8C4;
    --r:   8px;
    --rl:  12px;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ac-page {
    /* The theme's font, DM Sans, as on every other screen (K2.3 pt 16.1). Removed 2 Nov 2026 (K2.3 pt 16.1): the
       computer's own fonts only (-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif). */
    font-family: ${tokens.font.family};
    background: #F0F0EE;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  /* Card shell */
  .ac-card {
    background: var(--bg1);
    border: 0.5px solid var(--b1);
    border-radius: 16px;
    width: 380px;
    max-width: 100%;
    overflow: hidden;
  }

  .ac-head { padding: 28px 28px 0; text-align: center; }
  .ac-top { text-align: center;padding: 0 28px 0; }
  .ac-body { padding: 20px 28px 0;}
  .ac-footer { padding: 0 28px 10px; }

  /* Logo */
  .ac-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }
  .ac-logo img { max-width: 140px; max-height: 44px; object-fit: contain; }

  /* Badge */
  .ac-badge {
    font-size: 10px;
    padding: 3px 10px;
    border-radius: var(--r);
    font-weight: 500;
    display: inline-block;
    margin-bottom: 12px;
    background: var(--bi);
    color: var(--ti);
  }
  .ac-badge-i {
    background: var(--bw);
    color: var(--tw);
  }

  /* Powered-by */
  .ac-pw {
    font-size: 10px;
    color: var(--t3);
    text-align: center;
    padding: 8px 0 14px;
  }

  /* ── Reusable inner pieces (used by individual cards) ── */

  .ac-title { font-size: 18px; font-weight: 500; color: var(--t1); margin-bottom: 6px; }
  .ac-sub   { font-size: 13px; color: var(--t2); margin-bottom: 24px; line-height: 1.5; }
  .ac-hint  { font-size: 11px; color: var(--t3); margin-top: 12px; line-height: 1.5; text-align: center; }

  /* Status boxes */
  .ac-box         { border-radius: var(--r); padding: 14px; text-align: center; margin-bottom: 16px; }
  .ac-box-success { background: var(--bs); }
  .ac-box-error   { background: var(--bd); }
  .ac-box-warn    { background: var(--bw); }
  .ac-box-info    { background: var(--bg2); }

  .ac-box-title   { font-size: 14px; font-weight: 500; }
  .ac-box-sub     { font-size: 12px; margin-top: 4px; line-height: 1.5; }

  .ac-box-success .ac-box-title,
  .ac-box-success .ac-box-sub  { color: var(--ts); }
  .ac-box-error   .ac-box-title,
  .ac-box-error   .ac-box-sub  { color: var(--td); }
  .ac-box-warn    .ac-box-title,
  .ac-box-warn    .ac-box-sub  { color: var(--tw); }
  .ac-box-info    .ac-box-title,
  .ac-box-info    .ac-box-sub  { color: var(--t2); }

  /* Icon circles */
  .ac-icon {
    width: 48px; height: 48px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 16px;
  }
  .ac-icon-success { background: var(--bs); }
  .ac-icon-error   { background: var(--bd); }
  .ac-icon-warn    { background: var(--bw); }
  .ac-icon-info    { background: var(--bi); }

  /* Buttons */
  .ac-btn-primary {
    width: 100%; height: 44px; border-radius: var(--r);
    border: none; background: var(--t1); color: var(--bg1);
    font-size: 14px; font-weight: 500; cursor: pointer; font-family: inherit;
  }
  .ac-btn-primary:hover { opacity: 0.88; }

  .ac-btn-secondary {
    width: 100%; height: 44px; border-radius: var(--r);
    border: 0.5px solid var(--b1); background: var(--bg1); color: var(--t1);
    font-size: 14px; font-weight: 500; cursor: pointer; font-family: inherit;
  }
  .ac-btn-secondary:hover { background: var(--bg2); }

  /* Form field */
  .ac-field { margin-bottom: 16px; }
  .ac-field-label  { font-size: 12px; color: var(--t2); margin-bottom: 6px; font-weight: 500; display: block; }
  .ac-field-input  {
    width: 100%; height: 42px; border-radius: var(--r);
    border: 0.5px solid var(--b1); padding: 0 14px;
    font-size: 14px; background: var(--bg1); color: var(--t1);
    font-family: inherit; outline: none;
  }
  .ac-field-input::placeholder { color: var(--t3); }
  .ac-field-input[readonly]    { background: var(--bg2); color: var(--t2); }
  .ac-field-input.error        { border-color: var(--td); }
  .ac-field-err  { font-size: 11px; color: var(--td); margin-top: 4px; }
  .ac-field-link { font-size: 13px; color: var(--t2); margin-top: 20px;text-align: center; }
  .ac-field-hint { font-size: 11px; color: var(--t2); margin-top: 4px;text-align: center; }
  .ac-link{color: var(--ti);cursor: pointer;font-weight: 500;text-decoration: none;}

  /* Spinner centering */
  .ac-spinner {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 32px 0 24px;
    gap: 12px;
  }
  .ac-spinner-text { font-size: 13px; color: var(--t2); }

  .ac-divider {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 20px 0;
  }
  .ac-divider-line {
      flex: 1;
      height: 0.5px;
      background: var(--b1);
  }
  .ac-divider-text {
      font-size: 11px;
      color: var(--t3);
  }
  .ac-redirect {
    text-align: center;
    font-size: 13px;
    color: var(--t2);
  }    
`;

/* ─── Inject tokens once ─────────────────────────────────────────────────── */
let tokensInjected = false;
function injectTokens() {
  if (tokensInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.textContent = TOKEN_STYLE;
  document.head.appendChild(el);
  tokensInjected = true;
}

/* ─── AuthCard component ─────────────────────────────────────────────────── */
/**
 * @param {object}  props
 * @param {string}  [props.logoUrl]     - URL for the logo image
 * @param {string}  [props.badge]       - Short label shown above the title (e.g. "Customer account")
 * @param {string}  [props.poweredBy]   - Footer brand name; defaults to "Beetsol"
 * @param {React.ReactNode} props.children
 */
const AuthCard = ({ logoUrl, badge, poweredBy = 'Beetsol', children }) => {
  injectTokens();

  return (
    <div className="ac-page">
      <div className="ac-card">
        <div className="ac-head">
          {logoUrl && (
            <div className="ac-logo">
              <img src={logoUrl} alt="Logo" />
            </div>
          )}
          {badge && <div className="ac-badge">{badge}</div>}
        </div>

        {children}

        <div className="ac-pw">Powered by {poweredBy}</div>
      </div>
    </div>
  );
};

export default AuthCard;
